import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Typography from "@mui/material/Typography";
import axios from "axios";
import Cookies from "universal-cookie";
import { OutlinedTextField } from "../Components/CustomTextField";
import { getOrgInfo, getUserPrefrenceTimezone } from "../Api/Api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import "./Login.css";
import { ThemeProperties } from "../Theme/ThemeProperties";
import { getLocationFilter, getOrgFilter } from "../Api/MapApi";
import { SetMapCity, SetOrgFilter } from "../Actions/Map";
import { SetDiagnosticsCity } from "../Actions/Diagnostics";
import FindLocalTimezone from "../Helper/Operations/FindLocalTimezone";
import AppCache from "../Helper/Common/AppCache";
import { setUserID } from "../Helper/Common/Monitoring";
import LogoContainer from "./LogoContainer";
import { SetUserPermissions, SetPermissionsLoading } from "../Actions/PermissionActions";
import PermissionService from "../Services/PermissionService";
import CODE from "../Static/Constants/StatusCodes";
import { FEATURE_CODES } from "../Static/FeatureMapping";
import { getFeatures } from "../Api/RbacAdminApi";
import { ASSET_MANAGEMENT_LABEL } from "../Static/ModuleLabels";

function Copyright(props) {
  return (
    <Typography align="center" {...props} sx={{ fontSize: "12px", mt: 2 }}>
      {"Copyright © "}
      <Link color="inherit" href="https://coulomb.ai/">
        Coulomb AI
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function Login(props) {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [showPassword, setShowPassword] = React.useState(false);
  const isPassReset = useSelector((state) => state.PassChangedSuccess.value);

  const formatInputUserName = (event) => {
    setUsername(event.target.value.trim());
  };

  const formatInputPassword = (event) => {
    setPassword(event.target.value.trim());
  };

  const [errorText, setErrorText] = React.useState("");

  // If a logged-in paid user was force-logged-out because their org subscription
  // expired, SettingData.js sets this flag before redirecting here. Show the same
  // block message once, then clear it.
  useEffect(() => {
    if (sessionStorage.getItem("loginBlockedReason") === "subscription") {
      setErrorText("Account has been inactivated, please contact the admin");
      sessionStorage.removeItem("loginBlockedReason");
    }
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  /**
   * Complete the login process and redirect to dashboard
   * Centralized function to avoid code duplication
   */
  const completeLoginAndRedirect = (message = 'Redirect triggered') => {
    setTimeout(() => {
      // Hide loading indicator and set login state
      setIsLoggingIn(false);
      props.setLoggedIn(true);

      // If active dashboard is not Coulomb, navigate to that dashboard's route
      const activeDashboard = AppCache.getItem('activeDashboard') || 'coulomb';
      if (activeDashboard !== 'coulomb') {
        const storedDashboards = AppCache.getItem('availableDashboards') || [];
        const activeDash = storedDashboards.find(d => d.key === activeDashboard);
        navigate(activeDash?.route || '/custom-observability');
        return;
      }

      // Determine Coulomb landing page and navigate
      const pages = AppCache.getItem('pages') || [];
      const formattedPages = pages.map(item => item.toLowerCase());
      const landingPage = formattedPages.includes('overview') ? '/overview' : '/inventory';

      navigate(landingPage);
    }, 50);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsLoggingIn(true); // Show loading indicator
    const data = new FormData(event.currentTarget);

    var credentials = JSON.stringify({
      username: data.get("email").trim(),
      password: data.get("password").trim(),
    });

    var config = {
      method: "post",
      url: "/api/login",
      headers: {
        "Content-Type": "application/json",
      },
      data: credentials,
    };

    const userPermissions = ["view-twin", "view-config"];
    const adminPermissions = [
      "add-twin",
      "edit-twin",
      "view-twin",
      "add-config",
      "edit-config",
      "view-config",
    ];

    axios(config)
      .then((res) => {
        if (res.data.responseStatus.code === 200) {
          cookies.remove("finalCall", { path: "/" });
          cookies.set("name", res.data.loginData.name, { path: "/" });
          cookies.set("email", data.get("email").trim(), { path: "/" });
          setUserID(res.data.loginData.name);
          cookies.set("fleetID", res.data.loginData.fleetID, { path: "/" });
          const role = cookies.get("role");

          setErrorText("");
          const myPages = [
            {
              to: "/DigitalTwin",
              text: "Digital Twin List",
              mykey: "DigitalTwin",
              iconName: "navbarDigitalTwin",
              isActive: true,
              queryParam: {},
              permissions:
                role === "admin" ? adminPermissions : userPermissions,
            },
            {
              to: "/overview",
              text: "Home",
              mykey: "Overview",
              iconName: "overview",
              isActive: true,
              queryParam: {},
              permissions:
                role === "admin" ? adminPermissions : userPermissions,
            },
            {
              to: "/inventory",
              text: "Live Monitoring",
              mykey: "Inventory",
              iconName: "navbarDigitalTwin",
              isActive: true,
              queryParam: {},
              permissions:
                role === "admin" ? adminPermissions : userPermissions,
            },
            {
              to: "/asset-management",
              text: ASSET_MANAGEMENT_LABEL,
              mykey: "AssetManagement",
              iconName: "asset-management-icon",
              isActive: true,
              queryParam: {},
              permissions:
                role === "admin" ? adminPermissions : userPermissions,
            },
            {
              to: "/Map",
              iconName: "navbarMap",
              text: "Map",
              mykey: "Map",
              isActive: true,
              queryParam: {},
            },
            {
              to: "/Alerts",
              text: "Alerts",
              mykey: "Alerts",
              iconName: "navbarAlerts",
              isActive: true,
              queryParam: {},
            },
            {
              to: "/bms-faults",
              text: "Battery System Faults",
              mykey: "BMSAlertV2",
              isActive: true,
              queryParam: {},
              iconName: "bms-fault-icon",
            },
            {
              to: "/BatteryHealth",
              text: "Battery Health",
              mykey: "BatteryHealth",
              isActive: true,
              queryParam: {},
              iconName: "navbarBatteryHealth",
            },
            {
              to: "/battery-intelligence",
              text: "Battery Intelligence",
              mykey: "BatteryIntelligence",
              isActive: true,
              queryParam: {},
              iconName: "battery-intelligence-icon",
            },
            {
              to: "/Diagnostics",
              text: "Diagnostics",
              mykey: "Diagnostics",
              isActive: true,
              queryParam: {},
              iconName: "navbarDiagnostics",
            },
            {
              listItems: [
                {
                  to: "/ChargingAnalytics",
                  text: "Charging Analytics",
                  mykey: "ChargingAnalytics",
                  isActive: true,
                  queryParam: {},
                },
                {
                  to: "/PerformanceAnalytics",
                  text: "Performance",
                  mykey: "PerformanceAnalytics",
                  isActive: false,
                  queryParam: {},
                },
                {
                  to: "/AlertAnalytics",
                  text: "Alert Analytics",
                  mykey: "AlertAnalytics",
                  isActive: true,
                  queryParam: {},
                },
                {
                  to: "/DataQuality",
                  text: "Data Quality",
                  mykey: "DataQuality",
                  isActive: false,
                  queryParam: {},
                },
                {
                  to: "/DriverBehavior",
                  text: "Driver Behavior",
                  mykey: "DriverBehavior",
                  isActive: false,
                  queryParam: {},
                },
              ],
              title: "Analytics",
              iconName: "analytics",
            },
          ];

          AppCache.clearOnLogin();

          // Re-set role and permissions after clearing (clearOnLogin wipes all localStorage)
          AppCache.setItem("userRole", role);
          AppCache.setItem("userPermissions", role === "admin" ? adminPermissions : userPermissions);
          AppCache.setItem("permissions", role === "admin" ? adminPermissions : userPermissions);

          // Store initial pages without ResourceOnboarding
          // ResourceOnboarding will be added later based on RBAC version
          const newPagesMeta = [];
          myPages?.forEach((item) => {
            if (item?.listItems) {
              item.listItems?.map((page) => newPagesMeta.push(page));
            } else {
              newPagesMeta.push(item);
            }
          });
          AppCache.setItem("pagesMeta", newPagesMeta);
          AppCache.setItem("navigationCard", myPages);
        } else if (res.data.responseStatus.code === CODE.FORBIDDEN) {
          // Org subscription expired for this paid user. The /api/login endpoint
          // returns HTTP 200 with responseStatus.code 403 (its envelope
          // convention), so this branch runs in .then, not .catch. 403 is only
          // ever returned here for the subscription block.
          setErrorText(
            res.data.responseStatus.msg ||
              "Account has been inactivated, please contact the admin"
          );
          setIsLoggingIn(false); // Hide loading indicator on error
        } else {
          setErrorText("Invalid Login Id or password");
          setIsLoggingIn(false); // Hide loading indicator on error
        }
        return res.data.responseStatus.code;
      })
      .then((code) => {
        // Only run the post-login fetches when login actually succeeded.
        // On a blocked/invalid login there is no session, so calls like
        // getOrgInfo() would 401 and surface as an uncaught runtime error.
        if (code !== 200) return;
        getUserPrefrenceTimezone().then((res) => {
          if (res.responseStatus.code === 200) {
            if (res.response.response.preferenceData === null) {
              cookies.set("timeZone", res.response.response.primaryTimeZone, {
                path: "/",
              });
              cookies.set("isAutomaticTimezone", 0, {
                path: "/",
              });
            } else if (res.response.response.preferenceData !== null) {
              if (res.response.response.preferenceData.isAutomaticTime === 1) {
                let findLocalTimeZone = FindLocalTimezone();
                if (findLocalTimeZone !== undefined) {
                  cookies.set("timeZone", findLocalTimeZone["name"], {
                    path: "/",
                  });
                  cookies.set("isAutomaticTimezone", 1, {
                    path: "/",
                  });
                }
              } else if (
                res.response.response.preferenceData.timezonename !== null
              ) {
                cookies.set(
                  "timeZone",
                  res.response.response.preferenceData.timezonename,
                  {
                    path: "/",
                  }
                );
                cookies.set("isAutomaticTimezone", 0, {
                  path: "/",
                });
              }
            }
          }
        });

        getOrgInfo().then(async (res) => {
          if (res.responseStatus.code === 200) {
            cookies.set("orgName", res.response.orgName, { path: "/" });
            cookies.set("sessionId", new Date().getTime() / 1000, {
              path: "/",
            });
            cookies.set("amplitudeID", res.response.amplitudeID, {
              path: "/",
            });
            AppCache.setItem(
              "pages",
              res.response.metaInfo?.pagesToShow || [
                "Map",
                "Landing",
                "Alerts",
                "SpecificBattery",
                "DigitalTwin",
                "Diagnostics",
                "BatteryHealth",
                "chargingAnalytics",
                "alertAnalytics",
                "alertDrillDown",
              ]
            );

            // NEW: RBAC v2 Integration - Read from metaInfo.globalConfig
            const globalConfig = res.response.metaInfo?.globalConfig || {};
            const rbacVersion = globalConfig.rbacVersion || 'v1';
            // Ensure rbacEnabled is a boolean
            const rbacEnabled = globalConfig.rbacEnabled === true || globalConfig.rbacEnabled === 'true';

            // Store RBAC configuration
            AppCache.setItem('rbacVersion', rbacVersion);
            AppCache.setItem('rbacEnabled', rbacEnabled);

            // Store pre-applied system filters from globalConfig
            const preAppliedGlobalFilters = globalConfig.preAppliedGlobalFilters || {};
            AppCache.setItem('preAppliedGlobalFilters', preAppliedGlobalFilters);

            // Store global filter config from org meta
            const globalFilter = res.response.metaInfo?.globalFilter || [];
            AppCache.setItem('globalFilter', globalFilter);

            // Store per-page meta from org info
            const fleetPerformanceMeta = res.response.metaInfo?.FleetPerformance || {};
            AppCache.setItem('fleetPerformanceMeta', fleetPerformanceMeta);

            // Header branding (logo/width/background) from globalConfig - see Helper/Organization/Branding.js
            const branding = globalConfig.branding || {};
            AppCache.setItem('orgBranding', branding);

            AppCache.setItem('pingStatusThreshold', res.response.pingStatusThreshold);

            /**
             * Build and store custom observability platform navigation from org meta.
             *
             * customObsConfig has the shape:
             *   { pagesToShow: ['overview', 'inventory', 'digitalTwin'], overview: {...}, inventory: {...}, ... }
             *
             * pagesToShow keys are lowercase. Add new entries to CUSTOM_PAGE_MAP as
             * custom platform pages are built out.
             */
            const buildCustomNavigationFromOrgMeta = (customObsConfig, configKey, dashboardRoute) => {
              const customPagesToShow = customObsConfig.pagesToShow || [];
              if (customPagesToShow.length === 0) return;

              const userPermissions = AppCache.getItem('userPermissions');
              const baseRoute = dashboardRoute || '/custom-observability';

              // Map of pagesToShow key → nav item definition.
              // Extend this object as custom platform pages are implemented.
              // Entries with permissionRequired are filtered out when the user lacks that permission.
              const CUSTOM_PAGE_MAP = {
                overview: {
                  to: baseRoute,
                  text: 'Overview',
                  mykey: 'CustomOverview',
                  iconName: 'overview',
                  isActive: true,
                  queryParam: {},
                },
                inventory: {
                  to: `${baseRoute}/inventory`,
                  text: 'Inventory',
                  mykey: 'CustomInventory',
                  iconName: 'navbarDigitalTwin',
                  isActive: true,
                  queryParam: {},
                },
                custombmsalertv2: {
                  to: `${baseRoute}/bms-faults`,
                  text: 'Battery System Faults',
                  mykey: 'CustomBMSAlertV2',
                  iconName: 'bms-fault-icon',
                  isActive: true,
                  queryParam: {},
                  permissionRequired: FEATURE_CODES.INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS,
                },
              };

              const rbacPermissionsMap = PermissionService.getAllPermissions?.() || {};
              const isRbacV2Active =
                AppCache.getItem('rbacEnabled') && AppCache.getItem('rbacVersion') === 'v2';

              const customNavigationCard = customPagesToShow
                .map(key => CUSTOM_PAGE_MAP[key.toLowerCase()])
                .filter(Boolean)
                .filter(def => {
                  if (!def.permissionRequired) return true;
                  if (!isRbacV2Active) return true;
                  return rbacPermissionsMap[def.permissionRequired]?.includes('view');
                })
                .map(({ permissionRequired, ...def }) => ({ ...def, permissions: userPermissions }));

              const customPagesMeta = [];
              customNavigationCard.forEach(item => {
                if (item.listItems) {
                  item.listItems.forEach(subPage => customPagesMeta.push(subPage));
                } else {
                  customPagesMeta.push(item);
                }
              });

              // Store in per-dashboard maps so switching dashboards can restore the right nav
              const allCustomNavigationCards = AppCache.getItem('allCustomNavigationCards') || {};
              allCustomNavigationCards[configKey] = customNavigationCard;
              AppCache.setItem('allCustomNavigationCards', allCustomNavigationCards);

              const allCustomPagesMeta = AppCache.getItem('allCustomPagesMeta') || {};
              allCustomPagesMeta[configKey] = customPagesMeta;
              AppCache.setItem('allCustomPagesMeta', allCustomPagesMeta);
            };

            // Helper function to add FleetPerformance to navigation
            const addFleetPerformanceToNavigation = () => {
              const userPermissions = AppCache.getItem("userPermissions");
              const navigationCard = AppCache.getItem("navigationCard") || [];
              const fleetPerformancePage = {
                to: "/fleet-performance",
                text: "Fleet Performance",
                mykey: "FleetPerformance",
                isActive: true,
                queryParam: {},
                iconName: "fleet-performance-icon",
                permissions: userPermissions,
              };

              const alreadyExists = navigationCard.some(page => page.mykey === "FleetPerformance");
              if (!alreadyExists) {
                navigationCard.push(fleetPerformancePage);
                AppCache.setItem("navigationCard", navigationCard);

                const pagesMeta = AppCache.getItem("pagesMeta") || [];
                pagesMeta.push(fleetPerformancePage);
                AppCache.setItem("pagesMeta", pagesMeta);
              }
            };

            // Helper function to add Data menu (Data Upload + Data Download) to navigation
            const addDataMenuToNavigation = (includeUpload = true, includeDownload = true) => {
              const userPermissions = AppCache.getItem("userPermissions");
              const navigationCard = AppCache.getItem("navigationCard") || [];

              const dataUploadPage = {
                to: "/resource-onboarding",
                text: "Data Upload",
                mykey: "ResourceOnboarding",
                isActive: true,
                queryParam: {},
                iconName: "data-upload-icon",
                permissions: userPermissions,
              };

              const dataDownloadPage = {
                to: "/data-download",
                text: "Data Download",
                mykey: "DataDownload",
                isActive: true,
                queryParam: {},
                iconName: "data-download-icon",
                permissions: userPermissions,
              };

              const listItems = [];
              if (includeUpload) listItems.push(dataUploadPage);
              if (includeDownload) listItems.push(dataDownloadPage);

              if (listItems.length === 0) return;

              // Check if Data menu already exists to avoid duplicates
              const alreadyExists = navigationCard.some(
                (page) => page.title === "Data" || page.mykey === "ResourceOnboarding" || page.mykey === "DataDownload"
              );
              if (!alreadyExists) {
                // Add as dropdown parent
                navigationCard.push({
                  title: "Data",
                  iconName: "resource-onboarding",
                  listItems,
                });
                AppCache.setItem("navigationCard", navigationCard);

                // Add sub-routes to pagesMeta for App.jsx route generation
                const pagesMeta = AppCache.getItem("pagesMeta") || [];
                if (includeUpload) pagesMeta.push(dataUploadPage);
                if (includeDownload) pagesMeta.push(dataDownloadPage);
                AppCache.setItem("pagesMeta", pagesMeta);

                // Ensure both mykeys are in availablePages so sidebar visibility check passes
                const pages = AppCache.getItem("pages") || [];
                if (includeUpload && !pages.includes("ResourceOnboarding")) pages.push("ResourceOnboarding");
                if (includeDownload && !pages.includes("DataDownload")) pages.push("DataDownload");
                AppCache.setItem("pages", pages);
              }
            };

            // Helper function to add BatteryIntelligence to navigation
            const addBatteryIntelligenceToNavigation = () => {
              const userPermissions = AppCache.getItem("userPermissions");
              const navigationCard = AppCache.getItem("navigationCard") || [];
              const batteryIntelligencePage = {
                to: "/battery-intelligence",
                text: "Battery Intelligence",
                mykey: "BatteryIntelligence",
                isActive: true,
                queryParam: {},
                iconName: "battery-intelligence-icon",
                permissions: userPermissions,
              };

              const alreadyExists = navigationCard.some(page => page.mykey === "BatteryIntelligence");
              if (!alreadyExists) {
                navigationCard.push(batteryIntelligencePage);
                AppCache.setItem("navigationCard", navigationCard);

                const pagesMeta = AppCache.getItem("pagesMeta") || [];
                pagesMeta.push(batteryIntelligencePage);
                AppCache.setItem("pagesMeta", pagesMeta);
              }
            };
            // Keep backward-compatible alias
            const addResourceOnboardingToNavigation = () => addDataMenuToNavigation(true, true);

            // Maps Org Settings featureCodes to frontend nav item config.
            const ORG_SETTINGS_FEATURE_NAV_MAP = {
              SUPPORTED_LOCATIONS: {
                to: '/org-settings/supported-locations',
                text: 'Supported Locations',
                mykey: 'SupportedLocations',
                iconName: 'location-on-icon',
              },
              CLIENT_API_MANAGEMENT: {
                to: '/org-settings/client-api-management',
                text: 'Client API Management',
                mykey: 'ClientApiManagement',
                iconName: 'api-icon',
              },
            };

            // Builds (or extends) the "Org Settings" sidebar group with whichever child features the user can access.
            // Safe to call multiple times — deduplicates by route before pushing.
            const addOrgSettingsToNavigation = ({ hasSupportedLocations, hasClientApiManagement }) => {
              const userPermissions = AppCache.getItem("userPermissions");
              const navigationCard = AppCache.getItem("navigationCard") || [];
              const pagesMeta      = AppCache.getItem("pagesMeta")      || [];
              const pages          = AppCache.getItem("pages")          || [];

              const listItems = [];

              const addFeature = (featureKey) => {
                const cfg = ORG_SETTINGS_FEATURE_NAV_MAP[featureKey];
                if (!cfg) return;
                listItems.push({ ...cfg, isActive: true, queryParam: {}, permissions: userPermissions });
                if (!pagesMeta.some(p => p.to === cfg.to)) pagesMeta.push({ ...cfg, isActive: true, queryParam: {}, permissions: userPermissions });
                if (!pages.includes(cfg.mykey)) pages.push(cfg.mykey);
              };

              if (hasSupportedLocations)    addFeature('SUPPORTED_LOCATIONS');
              if (hasClientApiManagement)   addFeature('CLIENT_API_MANAGEMENT');

              if (listItems.length === 0) return;

              const existingGroup = navigationCard.find(p => p.title === 'Org Settings');
              if (existingGroup) {
                listItems.forEach(item => {
                  if (!existingGroup.listItems.some(e => e.to === item.to)) {
                    existingGroup.listItems.push(item);
                  }
                });
              } else {
                navigationCard.push({ title: 'Org Settings', iconName: 'org-settings-icon', listItems });
              }

              AppCache.setItem("navigationCard", navigationCard);
              AppCache.setItem("pagesMeta", pagesMeta);
              AppCache.setItem("pages", pages);
            };

            // Helper function to add AccessManagement as its own standalone sidebar dropdown.
            const addAccessManagementToNavigation = () => {
              const userPermissions = AppCache.getItem("userPermissions");
              const navigationCard = AppCache.getItem("navigationCard") || [];

              const rolesPage = {
                to: "/access-management/roles",
                text: "Roles & Permissions",
                mykey: "AccessManagement",
                iconName: "supervisor-account-icon",
                isActive: true,
                queryParam: {},
                permissions: userPermissions,
              };

              const usersPage = {
                to: "/access-management/users",
                text: "Manage Users",
                mykey: "AccessManagement",
                iconName: "key-icon",
                isActive: true,
                queryParam: {},
                permissions: userPermissions,
              };

              const alreadyExists = navigationCard.some(
                (page) => page.title === "Access Management" || page.mykey === "AccessManagement"
              );
              if (!alreadyExists) {
                // Add as dropdown parent to navigationCard
                navigationCard.push({
                  title: "Access Management",
                  iconName: "access-management-icon",
                  listItems: [rolesPage, usersPage],
                });
                AppCache.setItem("navigationCard", navigationCard);

                // Add both sub-routes to pagesMeta for App.jsx route generation
                const pagesMeta = AppCache.getItem("pagesMeta") || [];
                pagesMeta.push(rolesPage);
                pagesMeta.push(usersPage);
                AppCache.setItem("pagesMeta", pagesMeta);
              }
            };

            // Fetch RBAC v2 permissions if enabled
            if (rbacVersion === 'v2' && rbacEnabled) {
              try {
                dispatch(SetPermissionsLoading(true));
                const permissionsMap = await PermissionService.fetchAndStorePermissions();
                dispatch(SetUserPermissions(permissionsMap));

                // Store in localStorage for page refresh
                localStorage.setItem('rbac_permissions', JSON.stringify(permissionsMap));
                localStorage.setItem('rbac_version', 'v2');
                localStorage.setItem('rbac_enabled', 'true');

                dispatch(SetPermissionsLoading(false));

                // RBAC v2: Add FleetPerformance if user has view permission
                const hasFleetPerformancePermission = permissionsMap.FLEET_PERFORMANCE?.includes('view');
                if (hasFleetPerformancePermission) {
                  addFleetPerformanceToNavigation();
                }

                // RBAC v2: Add Data menu if user has view permission AND org has enabled it via pagesToShow
                const v2Pages = AppCache.getItem("pages") || [];
                const hasResourceOnboardingPermission = permissionsMap.RESOURCE_ONBOARDING?.includes('view') && v2Pages.includes("ResourceOnboarding");
                const hasDataDownloadPermission = permissionsMap.DATA_DOWNLOAD?.includes('view') && v2Pages.includes("DataDownload");
                if (hasResourceOnboardingPermission || hasDataDownloadPermission) {
                  addDataMenuToNavigation(hasResourceOnboardingPermission, hasDataDownloadPermission);
                }

                // RBAC v2: Add BatteryIntelligence if user has view permission
                const hasBatteryIntelligencePermission = permissionsMap.BATTERY_INTELLIGENCE?.includes('view');
                if (hasBatteryIntelligencePermission) {
                  addBatteryIntelligenceToNavigation();
                }

                // RBAC v2: Add AccessManagement if user has view permission
                const hasAccessManagementPermission = permissionsMap.ACCESS_MANAGEMENT?.includes('view');
                if (hasAccessManagementPermission) {
                  addAccessManagementToNavigation();
                }

                // RBAC v2: Register PlatformAlertsV2 in availablePages so the sidebar's
                // isPageVisible() check passes. The nav item itself is built dynamically in
                // MiniDrawer.buildComputedNavigation(), but that check also requires the mykey
                // to exist in the pages array.
                const hasPlatformAlertsPermission = permissionsMap.PLATFORM_ALERTS_V2?.includes('view');
                if (hasPlatformAlertsPermission) {
                  const pages = AppCache.getItem("pages") || [];
                  if (!pages.includes("PlatformAlertsV2")) {
                    pages.push("PlatformAlertsV2");
                    AppCache.setItem("pages", pages);
                  }
                }

                // RBAC v2: Build "Org Settings" group — ORG_SETTINGS gates the parent dropdown,
                // SUPPORTED_LOCATIONS gates the child item inside it.
                const hasOrgSettingsPermission = permissionsMap.ORG_SETTINGS?.includes('view');
                const hasSupportedLocationsPermission = permissionsMap.SUPPORTED_LOCATIONS?.includes('view')
                  && v2Pages.includes("SupportedLocations");
                const hasClientApiManagementPermission = permissionsMap.CLIENT_API_MANAGEMENT?.includes('view')
                  && v2Pages.includes("ClientApiManagement");
                if (hasOrgSettingsPermission) {
                  addOrgSettingsToNavigation({
                    hasSupportedLocations: hasSupportedLocationsPermission,
                    hasClientApiManagement: hasClientApiManagementPermission,
                  });
                }

                // Extract multi-dashboard config from org meta.
                // Backend may send customDashboards as an array (new format) or an object map (legacy).
                // Normalize to object map keyed by controlledByFeatureName so all downstream code works uniformly.
                const rawCustomDashboards = res.response.metaInfo?.customDashboards;
                const customDashboards = {};
                if (Array.isArray(rawCustomDashboards)) {
                  rawCustomDashboards.forEach(cfg => {
                    const cfgKey = cfg.controlledByFeatureName;
                    if (cfgKey) customDashboards[cfgKey] = cfg;
                  });
                } else if (rawCustomDashboards && typeof rawCustomDashboards === 'object') {
                  Object.assign(customDashboards, rawCustomDashboards);
                }
                AppCache.setItem('customDashboardsConfig', customDashboards);

                // Build available dashboards from "Dashboard" category features the user can view.
                // The features API is authoritative — no hardcoded feature code list on the frontend.
                const availableDashboards = [];
                // Track org-settings flags across both category groups before calling addOrgSettingsToNavigation.
                let orgSettingsFromFeatures = false;
                let supportedLocationsFromFeatures = false;
                let clientApiManagementFromFeatures = false;
                try {
                  const featuresRes = await getFeatures();
                  if (featuresRes?.responseStatus?.code === CODE.SUCCESS) {
                    const rawData = featuresRes.response?.data;
                    const groups = Array.isArray(rawData) ? rawData : [];
                    groups.forEach(({ category, features }) => {
                      const catLabel = typeof category === 'string'
                        ? category
                        : (category?.categoryName || category?.name || '');
                      if (catLabel === 'Dashboard' && Array.isArray(features)) {
                        features.forEach((feat) => {
                          if (!permissionsMap[feat.featureCode]?.includes('view')) return;

                          if (feat.featureCode === FEATURE_CODES.COULOMB_OBSERVABILITY_PLATFORM) {
                            availableDashboards.push({
                              featureCode: feat.featureCode,
                              featureName: feat.featureName || 'Coulomb',
                              key: 'coulomb',
                              route: '/',
                            });
                          } else {
                            // Match to a customDashboards entry by controlledByFeatureName
                            const dashboardEntry = Object.entries(customDashboards)
                              .find(([, cfg]) => cfg.controlledByFeatureName === feat.featureCode);
                            if (dashboardEntry) {
                              const [configKey, cfg] = dashboardEntry;
                              availableDashboards.push({
                                featureCode: feat.featureCode,
                                featureName: feat.featureName || configKey,
                                key: configKey,
                                route: cfg.route || '/custom-observability',
                              });
                            }
                          }
                        });
                      }

                      if (catLabel === 'Org Settings' && Array.isArray(features)) {
                        features.forEach((feat) => {
                          if (feat.featureCode === FEATURE_CODES.ORG_SETTINGS && permissionsMap[feat.featureCode]?.includes('view'))
                            orgSettingsFromFeatures = true;
                          if (feat.featureCode === FEATURE_CODES.SUPPORTED_LOCATIONS && permissionsMap[feat.featureCode]?.includes('view'))
                            supportedLocationsFromFeatures = true;
                          if (feat.featureCode === FEATURE_CODES.CLIENT_API_MANAGEMENT && permissionsMap[feat.featureCode]?.includes('view'))
                            clientApiManagementFromFeatures = true;
                        });
                      }
                    });

                    if (orgSettingsFromFeatures) {
                      addOrgSettingsToNavigation({
                        hasSupportedLocations: supportedLocationsFromFeatures,
                        hasClientApiManagement: clientApiManagementFromFeatures,
                      });
                    }
                  }
                } catch (err) {
                  // Fallback: derive list from permissions map + customDashboards config directly
                  if (permissionsMap[FEATURE_CODES.COULOMB_OBSERVABILITY_PLATFORM]?.includes('view')) {
                    availableDashboards.push({
                      featureCode: FEATURE_CODES.COULOMB_OBSERVABILITY_PLATFORM,
                      featureName: 'Coulomb',
                      key: 'coulomb',
                      route: '/',
                    });
                  }
                  Object.entries(customDashboards).forEach(([configKey, cfg]) => {
                    if (cfg.controlledByFeatureName && permissionsMap[cfg.controlledByFeatureName]?.includes('view')) {
                      availableDashboards.push({
                        featureCode: cfg.controlledByFeatureName,
                        featureName: configKey,
                        key: configKey,
                        route: cfg.route || '/custom-observability',
                      });
                    }
                  });
                }

                AppCache.setItem('availableDashboards', availableDashboards);

                const hasCoulombDashboard = availableDashboards.some(d => d.key === 'coulomb');
                const hasCustomDashboard = availableDashboards.some(d => d.key !== 'coulomb');
                AppCache.setItem('hasCoulombDashboard', hasCoulombDashboard);
                AppCache.setItem('hasCustomDashboard', hasCustomDashboard);

                // Default to Coulomb; use first accessible custom dashboard only when Coulomb is unavailable
                const firstCustomDashboard = availableDashboards.find(d => d.key !== 'coulomb');
                const activeDashboard = (!hasCoulombDashboard && firstCustomDashboard)
                  ? firstCustomDashboard.key
                  : 'coulomb';
                AppCache.setItem('activeDashboard', activeDashboard);

                // Build and store navigation for every accessible custom dashboard
                if (hasCustomDashboard) {
                  availableDashboards
                    .filter(d => d.key !== 'coulomb')
                    .forEach(d => {
                      const cfg = customDashboards[d.key];
                      if (cfg) buildCustomNavigationFromOrgMeta(cfg, d.key, d.route);
                    });

                  // Activate the custom dashboard config and navigation only when it is the active dashboard.
                  // When the user lands on Coulomb, leave customObservabilityConfig unset so page
                  // components on Coulomb routes never read a stale custom config.
                  if (activeDashboard !== 'coulomb' && customDashboards[activeDashboard]) {
                    AppCache.setItem('customObservabilityConfig', customDashboards[activeDashboard]);
                    const allNavs = AppCache.getItem('allCustomNavigationCards') || {};
                    AppCache.setItem('customNavigationCard', allNavs[activeDashboard] || []);
                    const allPagesMeta = AppCache.getItem('allCustomPagesMeta') || {};
                    AppCache.setItem('customPagesMeta', allPagesMeta[activeDashboard] || []);
                    AppCache.setItem('customPages', customDashboards[activeDashboard].pagesToShow || []);
                  }
                }

                // Complete login and redirect to dashboard
                completeLoginAndRedirect('Redirect triggered (v2 success)');
              } catch (error) {
                console.error('[Login] Failed to fetch RBAC v2 permissions:', error);
                // Fallback to v1 on error
                AppCache.setItem('rbacVersion', 'v1');
                dispatch(SetPermissionsLoading(false));

                // Complete login with fallback message
                completeLoginAndRedirect('Redirect triggered (v2 error - fallback to v1)');
              }
            } else {
              // RBAC v1: Add FleetPerformance and ResourceOnboarding only if user is admin
              const userRole = AppCache.getItem("userRole");
              if (userRole === "admin") {
                addFleetPerformanceToNavigation();
                const pages = AppCache.getItem("pages") || [];
                const includeUpload = pages.includes("ResourceOnboarding");
                const includeDownload = pages.includes("DataDownload");
                if (includeUpload || includeDownload) {
                  addDataMenuToNavigation(includeUpload, includeDownload);
                }
              }

              // Complete login and redirect to dashboard
              completeLoginAndRedirect('Redirect triggered (v1)');
            }
          }
        });

        getOrgFilter().then((res) => {
          if (res.responseStatus.code === 200) {
            let fleetName = res.response.filterData.find(
              (i) => i.type === "fleetName"
            );
            if (fleetName !== undefined) {
              localStorage.setItem("OrgFilter", fleetName.value);
              dispatch(SetOrgFilter(fleetName.value));
            }
          }
        });

        getLocationFilter().then((res) => {
          if (res.responseStatus.code === 200) {
            AppCache.setItem("LocationFilter", res.response.locationFilterData);
            dispatch(SetMapCity(res.response.locationFilterData));
            dispatch(SetDiagnosticsCity(res.response.locationFilterData));
          }
        });
      })

      .catch(function (error) {
        console.error('[Login] Login error:', error);
        setIsLoggingIn(false); // Hide loading indicator on network error
        setErrorText("An error occurred during login. Please try again.");
      });
  };

  return (
    <Box
      sx={{
        display: "table",
        position: "absolute",
        top: 0,
        left: 0,
        height: "100%",
        width: "100%",
        bgcolor: "#F2F2F4",
      }}
    >
      <Box
        sx={{
          display: "table-cell",
          verticalAlign: "middle",
        }}
      >
        <Box
          sx={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "500px",
            mb: 2,
            //   padding: "0px 56px ",
          }}
        >
          {isPassReset && (
            <div className={`passChangedSuccess`}>
              <Typography variant="textPasswordReset" align="center">
                Password is changed successfully!
              </Typography>
            </div>
          )}
        </Box>

        <LogoContainer />

        <Box
          sx={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "450px",
            border: "1px solid #DDD7FF",
            borderRadius: "1.2rem",
            backgroundColor: "#fff",
            padding: "30px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="paperHeader"
              sx={{
                fontSize: "20px",
              }}
            >
              Login to Battery Observability Platform
            </Typography>
            <br></br>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              <OutlinedTextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Login ID"
                value={username}
                onChange={(e) => formatInputUserName(e)}
                name="email"
                autoComplete="email"
                autoFocus
                error={errorText.length === 0 ? false : true}
              />
              <OutlinedTextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                value={password || null}
                onChange={(e) => formatInputPassword(e)}
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                error={errorText.length === 0 ? false : true}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ m: "5px 0px 10px" }}>
                <Typography variant="textSubtitleRed">{errorText}</Typography>
              </Box>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <Typography
                    variant="textSubtitle"
                    onClick={() => navigate("/reset_password")}
                    sx={{ cursor: "pointer", color: ThemeProperties.purple }}
                  >
                    Reset Password
                  </Typography>
                </div>

                <div>
                  <Typography
                    variant="textSubtitle"
                    onClick={() => navigate("/forgot_password")}
                    sx={{ cursor: "pointer", color: ThemeProperties.purple }}
                  >
                    Forgot Password?
                  </Typography>
                </div>
              </div>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoggingIn}
                sx={{ mt: 3, mb: 2, fontSize: 13, borderRadius: "1rem" }}
              >
                {isLoggingIn ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
        <Copyright sx={{ mt: 4, mb: 4 }} />
      </Box>
    </Box>
  );
}
